import { useMemo } from "react";
import { isEntitlementActive, resolveModuleCatalog } from "@/modules/catalogModulos";
import { entitlementsFromSystemInfo } from "@/modules/entitlementsFromSystemInfo";
import { commercialPlanLabel } from "@/lib/commercialLicenseRequests";
import {
  moduleExpiryHintForModule,
} from "@/lib/moduleExpiryHint";

function hintClassName(kind) {
  if (kind === "expired") {
    return "text-[10px] text-red-800 font-medium mt-0.5 m-0";
  }
  return "text-[10px] text-slate-500 mt-0.5 m-0";
}

/**
 * Lista de módulos activos bajo «Licencia actual» con aviso de vigencia solo ≤15 días o vencido.
 * Muestra plan Básico/Profesional cuando está disponible en systemInfo.
 */
export default function ActiveLicenseModulesList({ systemInfo, moduleCatalog: catalogProp }) {
  const moduleCatalog = catalogProp || resolveModuleCatalog(systemInfo || {});
  const commercialPlan = systemInfo?.license?.commercial_plan
    || systemInfo?.license?.payload?.commercial_plan
    || systemInfo?.organization_subscription?.commercial_plan;
  const orgSub = systemInfo?.organization_subscription;

  const ctx = useMemo(() => {
    const state = entitlementsFromSystemInfo(systemInfo);
    return {
      entitlements: state.entitlements,
      modulePermissions: systemInfo?.module_permissions || {},
    };
  }, [systemInfo]);

  const rows = useMemo(() => {
    return moduleCatalog
      .filter((mod) => isEntitlementActive(mod.id, ctx))
      .map((mod) => ({
        id: mod.id,
        label: mod.label || mod.id,
        hint: moduleExpiryHintForModule(systemInfo, mod.id),
      }));
  }, [moduleCatalog, ctx, systemInfo]);

  if (!commercialPlan && !rows.length) return null;

  return (
    <div
      className="mt-3 rounded-sm border border-slate-200 bg-slate-50 px-3 py-2 text-[11px] text-slate-700"
      data-testid="active-license-modules-list"
    >
      {commercialPlan ? (
        <p className="font-semibold text-slate-800 m-0 mb-1.5" data-testid="active-license-plan-label">
          Plan {commercialPlanLabel(commercialPlan)}
          {orgSub?.billing_cadence ? (
            <span className="font-normal text-slate-600"> · {orgSub.billing_cadence === "monthly" ? "Mensual" : "Anual"}</span>
          ) : null}
        </p>
      ) : (
        <p className="font-semibold text-slate-800 m-0 mb-1.5">Módulos activos</p>
      )}
      {rows.length ? (
        <ul className="space-y-1.5 m-0 p-0 list-none">
          {rows.map((row) => (
            <li key={row.id} data-testid={`active-license-module-${row.id}`}>
              <span className="text-slate-800">{row.label}</span>
              {row.hint ? (
                <p className={hintClassName(row.hint.kind)} data-testid={`active-license-module-hint-${row.id}`}>
                  {row.hint.text}
                </p>
              ) : null}
            </li>
          ))}
        </ul>
      ) : null}
    </div>
  );
}
